"use client";

import {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import { MediaMusic } from "@/components/music/media-music";
import { useVideoAutoplay } from "@/components/providers/video-autoplay-provider";
import { AppImage as Image } from "@/components/ui/app-image";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselDots,
  CarouselItem,
} from "@/components/ui/carousel";
import {
  MediaSoundButton,
  type MediaSoundPlacement,
} from "@/components/ui/media-sound-button";
import { PosterVideo } from "@/components/ui/poster-video";
import type { MediaData } from "@/db/schema/hiive";
import { ASPECT_RATIO_CLASS } from "@/lib/aspect-ratios";
import { haptic } from "@/lib/haptic";
import type { NormalizedMediaAsset } from "@/lib/media";
import { toRenderableMedia } from "@/lib/media";
import type { MusicAttachment } from "@/lib/music/attachment";
import { cn } from "@/lib/utils";

interface BannerCarouselProps {
  media: MediaData[];
  altName: string;
  /** Corner of the sound control — bottom-right unless the bottom is taken. */
  volumeButtonPosition?: MediaSoundPlacement;
  aspectRatio?: string;
  emptyState?: ReactNode;
  dotsClassName?: string;
  /**
   * Optional song for the banner's PHOTOS (event / experience banner music):
   * plays Instagram-style while a still slide shows; video slides keep their
   * own sound.
   */
  music?: MusicAttachment | null;
  /** Moves the song's credit pill (bottom-left) clear of a hero's overlays. */
  musicCreditClassName?: string;
  /** Nudges the speaker (video or song) — e.g. above a detail page's sheet. */
  soundButtonClassName?: string;
}

export function BannerCarousel({
  media,
  altName,
  volumeButtonPosition = "bottom-right",
  aspectRatio = ASPECT_RATIO_CLASS.portrait4By5,
  emptyState,
  dotsClassName,
  music = null,
  musicCreditClassName,
  soundButtonClassName,
}: BannerCarouselProps) {
  const carouselId = useId();
  const { isMuted } = useVideoAutoplay();
  const [emblaApi, setEmblaApi] = useState<CarouselApi | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  /**
   * Normalized once so render stays cheap. Note: no dedupe here — `media` is
   * a single form-edited array (max 6, one entry per slide), so the same
   * asset never appears twice; repeated downloads of one file in the wild
   * are a cache story (devtools "Disable cache", missing long Cache-Control
   * on the media host), not a duplicate-slide story.
   */
  const slides = useMemo(() => {
    const out: { key: string; renderable: NormalizedMediaAsset }[] = [];
    media.forEach((item, index) => {
      const renderable = toRenderableMedia(item);
      if (!renderable) return;
      out.push({ key: `${renderable.id}-${index}`, renderable });
    });
    return out;
  }, [media]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setActiveIndex(emblaApi.selectedScrollSnap());
    onSelect();
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  /**
   * Only the visible slide and its neighbours mount media. Embla keeps every
   * `CarouselItem` in the DOM (off-screen via transform, not display:none),
   * so rendering an `<Image>` per slide downloads the whole gallery — full
   * files, since images are unoptimized — before the user ever swipes.
   * Far slides keep an empty box of identical size so layout never shifts.
   */
  const isSlideNear = (index: number) => {
    if (slides.length <= 3) return true;
    const forward = (index - activeIndex + slides.length) % slides.length;
    const backward = (activeIndex - index + slides.length) % slides.length;
    return Math.min(forward, backward) <= 1;
  };

  if (!media.length) {
    if (emptyState) return <>{emptyState}</>;

    return (
      <section
        className={cn(
          "flex w-full items-center justify-center bg-surface text-text-secondary",
          aspectRatio,
        )}
      >
        <div className="text-center">
          <div className="mb-3 text-4xl">📸</div>
          <p className="text-sm">Media coming soon</p>
        </div>
      </section>
    );
  }

  return (
    <Carousel
      className="w-full"
      opts={{
        loop: true,
      }}
      setApi={setEmblaApi}
    >
      <CarouselContent>
        {slides.map(({ key, renderable }, index) => {
          const id = `${carouselId}banner-${index}`;
          const isVideo = renderable.kind === "video";
          const muted = isMuted(id);

          return (
            <CarouselItem key={key}>
              <section
                className={cn("relative w-full", aspectRatio)}
                aria-label={`${altName} media ${index + 1}`}
              >
                {isSlideNear(index) ? (
                  <BannerMedia
                    audioId={id}
                    item={renderable}
                    altName={altName}
                    slideNumber={index + 1}
                    slideCount={slides.length}
                    muted={muted}
                    priority={index === 0}
                  />
                ) : (
                  <div
                    className="absolute inset-0 bg-surface-alt"
                    aria-hidden
                  />
                )}

                {isVideo ? (
                  <MediaSoundButton
                    audioId={id}
                    placement={volumeButtonPosition}
                    className={soundButtonClassName}
                  />
                ) : null}
              </section>
            </CarouselItem>
          );
        })}
      </CarouselContent>

      {music ? (
        <MediaMusic
          audioId={`${carouselId}banner-music`}
          music={music}
          active={slides[activeIndex]?.renderable.kind === "image"}
          placement={volumeButtonPosition}
          speakerClassName={soundButtonClassName}
          className={cn(
            // Clear of the centred paging dots (at most ~100px wide).
            slides.length > 1 && "max-w-[calc(50%-4.5rem)]",
            musicCreditClassName,
          )}
        />
      ) : null}

      {slides.length > 1 ? (
        <CarouselDots
          className={cn(
            "bottom-4 [&_button]:border-white [&_button]:bg-white/50 [&_button[aria-selected=true]]:bg-white",
            dotsClassName,
          )}
          onClick={() => haptic("selection")}
        />
      ) : null}
    </Carousel>
  );
}

interface BannerMediaProps {
  audioId: string;
  item: NormalizedMediaAsset;
  altName: string;
  slideNumber: number;
  slideCount: number;
  muted?: boolean;
  priority?: boolean;
}

function BannerMedia({
  audioId,
  item,
  altName,
  slideNumber,
  slideCount,
  muted = true,
  priority = false,
}: BannerMediaProps) {
  const { register } = useVideoAutoplay();
  const setVideoRef = useCallback(
    (element: HTMLVideoElement | null) => register(audioId, element),
    [audioId, register],
  );
  const isVideo = item.kind === "video";

  return (
    <div className="absolute inset-0 h-full w-full">
      {isVideo ? (
        <PosterVideo
          ref={setVideoRef}
          src={item.url}
          mimeType={item.mimeType || "video/mp4"}
          muted={muted}
          playsInline
          loop
          posterUrl={item.poster?.url}
          blurHash={item.blurHash}
          priority={priority}
          preload="auto"
        />
      ) : (
        <Image
          src={item.url}
          /*
           * Alt text is the only description Google Images has for these
           * files: the keys are content-hashed, so the filename says nothing,
           * and the surrounding page text is client-rendered. Every slide in a
           * gallery used to carry the identical string "<name> media", which
           * gives a ranker one description to split across a dozen images and
           * gives a screen-reader user the word "media" a dozen times. Number
           * them, and only when there is more than one to tell apart.
           */
          alt={
            slideCount > 1
              ? `${altName} — photo ${slideNumber} of ${slideCount}`
              : altName
          }
          fill
          className="object-cover"
          sizes="(max-width: 768px) 90vw, 318px"
          priority={priority}
        />
      )}
    </div>
  );
}
